import { useState, useEffect, useRef } from "react";
import Icon from "../Icon";
import { BtnGhost } from "../Atoms";
import { AG_TIPO_CLR, AG_ORIGENES } from "../../constants";
import { fmtHours } from "../../utils";
import { hhmm, hhmmss } from "../../utils/hora";

// El cronometro de la tarea en curso, en la cabecera. Hermano del indicador de
// fichaje que tiene al lado y con el mismo trato: punto de color, contador
// corriendo y un desplegable al pulsar.
//
// Solo se pinta FUERA de Inicio. Alli esta el widget grande con lo mismo, y dos
// contadores de la misma tarea en la misma pantalla es informacion duplicada que
// ademas puede desincronizarse un segundo y hacer dudar de las dos.
//
// El clic no para nada directamente: abre el desplegable. En la cabecera esta
// siempre visible y un roce accidental cortaria un tramo de trabajo real.
//
// Dentro del desplegable estan las MISMAS salidas que en el widget del panel de
// inicio, incluidas empezar una tarea y reanudar una en pausa: tener que viajar
// a Inicio para volver a arrancar lo que acabas de pausar aqui mismo era el
// rodeo que hacia que la gente dejase el cronometro parado.

// En miniatura el contador va en HORAS Y MINUTOS. Los segundos corriendo en la
// cabecera no dicen nada que se vaya a usar y llaman la atencion sola; el
// cronometro al segundo esta en el panel de inicio, que es donde se mira cuando
// de verdad importa. El valor exacto sigue estando en el title del boton.
const corto = ms => {
  const min = Math.max(0, Math.floor(ms / 60000));
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:`
       + `${String(min % 60).padStart(2, "0")}`;
};

export default function CronometroHeaderButton({ crono, dentro = false, onIrAInicio, onCambiarDeTarea }) {
  const [abierto, setAbierto] = useState(false);
  const [ahora, setAhora] = useState(Date.now());
  const ref = useRef(null);

  const activo = crono?.activo || null;

  // Los hooks van TODOS antes del return temprano: colocar uno despues cambia
  // su numero entre renders y React lanza el error 310.
  // Cada 15 s basta: aqui el contador va en minutos, y repintar la cabecera
  // entera una vez por segundo era trabajo para no cambiar nada en pantalla.
  useEffect(() => {
    if (!activo) return;
    const id = setInterval(() => setAhora(Date.now()), 15000);
    return () => clearInterval(id);
  }, [activo]);

  useEffect(() => {
    if (!abierto) return;
    const fuera = e => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [abierto]);

  // Sin nada en marcha ni en pausa no se pinta: una cabecera con un 00:00:00
  // permanente es ruido. Y si al servidor le falta la migracion, tampoco.
  const pausados = crono?.pausados || [];
  if (!crono || crono.disponible === false || (!activo && !pausados.length)) return null;

  const cl = activo ? (AG_TIPO_CLR[activo.type] || "var(--accent)") : "#f59e0b";
  const ms = activo ? crono.msDe(activo, ahora) : 0;
  const org = activo && AG_ORIGENES[activo.origen];

  const titulo = activo
    ? `${activo.description} · ${hhmmss(ms)}`
    : `${pausados.length} tarea${pausados.length === 1 ? "" : "s"} en pausa`;

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button aria-label={titulo} className="btn" onClick={() => setAbierto(p => !p)} title={titulo}
        style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${cl}55`,
          background: abierto ? `${cl}1f` : "var(--surface)", color: "var(--text2)",
          cursor: "pointer", display: "flex", alignItems: "center", gap: 7,
          fontSize: 11.5, fontFamily: "var(--font)" }}>
        <Icon name="hourglass-split" size={12} color={cl} />
        <span className="m-hide" style={{ fontWeight: 600, color: cl,
          fontVariantNumeric: "tabular-nums" }}>
          {activo ? corto(ms) : `${pausados.length} en pausa`}
        </span>
      </button>

      {abierto && (
        <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 60,
          minWidth: 248, maxWidth: "min(340px, calc(100vw - 24px))",
          background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 11,
          boxShadow: "var(--shadow-lg)", padding: 12,
          display: "flex", flexDirection: "column", gap: 9 }}>

          {activo ? (
            <>
              <div style={{ fontSize: 11, color: "var(--text3)", lineHeight: 1.7 }}>
                <b style={{ color: "var(--text)", fontSize: 12 }}>{activo.description}</b><br />
                {activo.type}
                {org && activo.origen !== "ninguno" && (
                  <> · {activo.etiqueta || org.label}</>
                )}
                <br />
                Lleva <b style={{ color: cl }}>{corto(ms)}</b>
                {activo.tramos > 1 && <> en {activo.tramos} tramos</>}
                {activo.tramo_desde && <> · desde las {hhmm(activo.tramo_desde, "--:--")}</>}
              </div>

              {/* Las MISMAS tres salidas que el widget del panel de inicio, en
                  el mismo orden: que la accion dependa de desde donde abras el
                  cronometro es lo que hacia dudar de cual era cual. */}
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={async () => { setAbierto(false); await crono.pausar(activo.id); }}
                  disabled={crono.guardando}
                  style={{ ...accion, background: "#f59e0b" }}>
                  <Icon name="pause-circle-fill" size={13} color="#fff" />Pausar
                </button>
                {/* Interrupcion en un solo gesto: pausa y abre el buscador de
                    tareas, que vive en el panel de inicio. */}
                <button disabled={crono.guardando}
                  onClick={async () => {
                    setAbierto(false);
                    if (await crono.pausar(activo.id)) onCambiarDeTarea?.();
                  }}
                  style={{ ...accion, background: "var(--accent)" }}>
                  <Icon name="arrow-repeat" size={13} color="#fff" />Cambiar
                </button>
              </div>
              {/* Sin `disabled`: BtnGhost no lo lleva, y el candado real es el
                  del hook, que ignora una segunda orden con otra en vuelo. */}
              <BtnGhost onClick={async () => { setAbierto(false); await crono.parar(activo.id); }}
                style={{ justifyContent: "center", width: "100%" }}>
                <Icon name="journal-text" size={12} color="var(--text3)" />Terminar y anotar
              </BtnGhost>
            </>
          ) : (
            <div style={{ fontSize: 11, color: "var(--text3)", lineHeight: 1.7 }}>
              Nada en marcha.<br />
              {pausados.length} tarea{pausados.length === 1 ? "" : "s"} en pausa,{" "}
              <b style={{ color: "var(--accent)" }}>
                {fmtHours(pausados.reduce((s, p) => s + crono.msDe(p, ahora), 0) / 3600000)}
              </b>{" "}sin anotar.
            </div>
          )}

          {/* Los borradores en pausa, con su boton de reanudar. Se listan
              tambien con otra tarea en marcha: reanudar pausa la de antes, que
              es exactamente el gesto de volver a lo que se dejo a medias. */}
          {!!pausados.length && (
            <div style={{ borderTop: "1px solid var(--border2)", paddingTop: 8,
              display: "flex", flexDirection: "column", gap: 4 }}>
              {pausados.map(p => {
                const clp = AG_TIPO_CLR[p.type] || "var(--accent)";
                return (
                  <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 7,
                    padding: "5px 7px", borderRadius: 7, background: "var(--bg)" }}>
                    <span style={{ width: 6, height: 6, borderRadius: 2, background: clp,
                      flexShrink: 0 }} />
                    <span title={p.description} style={{ flex: 1, minWidth: 0, fontSize: 11.5,
                      fontWeight: 500, color: "var(--text)", overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.description}</span>
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: clp,
                      whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                      {corto(crono.msDe(p, ahora))}
                    </span>
                    {/* Fuera de jornada no se ofrece: el servidor devolveria un
                        409 y el boton solo serviria para llevarse un error. */}
                    {dentro && (
                      <button aria-label="Reanudar esta tarea" className="btn" title="Reanudar esta tarea"
                        onClick={async () => { setAbierto(false); await crono.reanudar(p.id); }}
                        disabled={crono.guardando} style={iconBtn}>
                        <Icon name="play-fill" size={14} color="var(--ok-text)" />
                      </button>
                    )}
                    <button aria-label="Anotar en el cuaderno y cerrar" className="btn" title="Anotar en el cuaderno y cerrar"
                      onClick={async () => { setAbierto(false); await crono.parar(p.id); }}
                      disabled={crono.guardando} style={iconBtn}>
                      <Icon name="journal-text" size={12} color="var(--text3)" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Empezar de cero sigue pasando por el buscador de tareas del panel
              de inicio: es un formulario entero y no cabe en un desplegable de
              la cabecera. El viaje lo hace el boton, no el usuario. */}
          {!activo && dentro && (
            <button onClick={() => { setAbierto(false); onCambiarDeTarea?.(); }}
              disabled={crono.guardando}
              style={{ ...accion, background: "var(--green)", width: "100%" }}>
              <Icon name="play-fill" size={14} color="#fff" />Empezar una tarea
            </button>
          )}

          <button className="btn" onClick={() => { setAbierto(false); onIrAInicio?.(); }}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0,
              fontFamily: "var(--font)", fontSize: 11, color: "var(--accent)",
              textAlign: "left" }}>
            Ver en el panel de inicio →
          </button>
        </div>
      )}
    </div>
  );
}

const iconBtn = {
  background: "transparent", border: "none", cursor: "pointer", padding: 0, display: "flex",
};

const accion = {
  flex: 1, padding: "8px 0", borderRadius: 8, border: "none", cursor: "pointer",
  fontSize: 12, fontWeight: 700, color: "#fff", fontFamily: "var(--font)",
  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
};
